import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Briefcase, Layers, Star, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { StatCard } from './AdminLayout';
import { projectsApi } from '../../api/projects';
import { Link } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const IMG_BASE = BASE_URL.replace('/api', '');

export function AdminOverview() {
    const { language } = useLanguage();
    const isRtl = language === 'ar';
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        projectsApi.getAll()
            .then(data => setProjects(Array.isArray(data) ? data : []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const totalProjects = projects.length;
    const withImages = projects.filter(p => p.images?.length > 0).length;
    const featured = projects.filter(p => p.isFeatured).length;
    const categories = [...new Set(projects.map(p => p.category).filter(Boolean))].length;
    const recent = [...projects].reverse().slice(0, 5);

    const categoryColors = {
        'Interior': '#6366f1',
        'Construction': '#f59e0b',
        'Facades': '#10b981',
        'Finishing': '#ef4444',
    };

    return (
        <div className="admin-page-content" dir={isRtl ? 'rtl' : 'ltr'}>
            <header className="admin-page-header">
                <div>
                    <h1>{isRtl ? 'لوحة التحكم' : 'Dashboard Overview'}</h1>
                    <p style={{ color: '#64748b' }}>{isRtl ? 'أهلاً بك، إليك ملخص المشاريع' : 'Welcome back — here is your projects summary.'}</p>
                </div>
                <a href="/" target="_blank" rel="noreferrer" className="btn-add-primary">
                    <ExternalLink size={18} />
                    <span>{isRtl ? 'معاينة الموقع' : 'View Live Site'}</span>
                </a>
            </header>

            {/* Stats */}
            <div className="admin-stats">
                <StatCard icon={<Briefcase size={24} />} count={loading ? '...' : totalProjects} label={isRtl ? 'إجمالي المشاريع' : 'Total Projects'} />
                <StatCard icon={<Layers size={24} />} count={loading ? '...' : categories} label={isRtl ? 'التصنيفات' : 'Categories'} />
                <StatCard icon={<ImageIcon size={24} />} count={loading ? '...' : withImages} label={isRtl ? 'مشاريع بصور' : 'With Images'} />
                <StatCard icon={<Star size={24} />} count={loading ? '...' : featured} label={isRtl ? 'مشاريع مميزة' : 'Featured'} />
            </div>

            {/* Category Breakdown */}
            {!loading && projects.length > 0 && (
                <div className="admin-content-card" style={{ marginBottom: 24 }}>
                    <div className="table-header">
                        <h3 style={{ fontWeight: 800 }}>{isRtl ? 'توزيع المشاريع' : 'Projects by Category'}</h3>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '16px 0' }}>
                        {Object.entries(
                            projects.reduce((acc, p) => {
                                const cat = p.category || 'Other';
                                acc[cat] = (acc[cat] || 0) + 1;
                                return acc;
                            }, {})
                        ).map(([cat, count]) => (
                            <div key={cat} style={{ flex: 1, minWidth: 120, background: '#f8fafc', borderRadius: 12, padding: '20px 16px', textAlign: 'center', borderTop: `4px solid ${categoryColors[cat] || '#94a3b8'}` }}>
                                <div style={{ fontSize: 28, fontWeight: 800, color: categoryColors[cat] || '#94a3b8' }}>{count}</div>
                                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{cat}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recent Projects */}
            <div className="admin-content-card">
                <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontWeight: 800 }}>{isRtl ? 'آخر المشاريع المضافة' : 'Recently Added Projects'}</h3>
                    <Link to="/admin/projects" style={{ color: '#ef4444', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
                        {isRtl ? 'عرض الكل ←' : 'View All →'}
                    </Link>
                </div>
                {loading ? (
                    <p style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Loading...</p>
                ) : projects.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <p style={{ color: '#94a3b8', marginBottom: 16 }}>{isRtl ? 'لا توجد مشاريع بعد' : 'No projects yet'}</p>
                        <Link to="/admin/projects" className="btn-add-primary">
                            {isRtl ? '+ أضف أول مشروع' : '+ Add First Project'}
                        </Link>
                    </div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>{isRtl ? 'المشروع' : 'Project'}</th>
                                <th>{isRtl ? 'التصنيف' : 'Category'}</th>
                                <th>{isRtl ? 'الموقع' : 'Location'}</th>
                                <th>{isRtl ? 'الصور' : 'Images'}</th>
                                <th>{isRtl ? 'سنة' : 'Year'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.map(p => {
                                const cover = p.images?.find(i => i.isPrimary) || p.images?.[0];
                                return (
                                    <tr key={p.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                {cover
                                                    ? <img src={`${IMG_BASE}${cover.imageUrl}`} alt={p.title} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                                                    : <div style={{ width: 44, height: 44, background: '#f1f5f9', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ImageIcon size={16} color="#94a3b8" /></div>
                                                }
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.title}</div>
                                                    {p.titleEn && <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.titleEn}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ background: `${categoryColors[p.category] || '#94a3b8'}20`, color: categoryColors[p.category] || '#94a3b8', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                                                {p.category || '—'}
                                            </span>
                                        </td>
                                        <td style={{ color: '#64748b', fontSize: 13 }}>{p.location || '—'}</td>
                                        <td>
                                            <span style={{ fontWeight: 700, color: p.images?.length > 0 ? '#10b981' : '#94a3b8' }}>
                                                {p.images?.length || 0} {isRtl ? 'صور' : 'imgs'}
                                            </span>
                                        </td>
                                        <td style={{ color: '#64748b', fontSize: 13 }}>
                                            {p.completedAt ? new Date(p.completedAt).getFullYear() : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
